import { useState, useEffect } from "react";
import {
  getHeroForAdmin,
  createHero,
  updateHero,
  deleteHero,
} from "../../../services/heroService";
// @ts-ignore
import uploadService from "../../../services/uploadService";

interface HeroSection {
  id: string;
  page: string;
  badge?: {
    text: string;
    icon: string;
  };
  heading: string;
  subheading: string;
  description: string;
  backgroundImage: string;
  buttons: Array<{
    text: string;
    url: string;
    actionType: "modal" | "url" | "scroll";
    style: "primary" | "secondary" | "outline";
    order: number;
    isVisible: boolean;
  }>;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const HeroSectionManagement = () => {
  const [heros, setHeros] = useState<HeroSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingHero, setEditingHero] = useState<HeroSection | null>(null);
  const [formData, setFormData] = useState({
    page: "",
    badge: { text: "", icon: "" },
    heading: "",
    subheading: "",
    description: "",
    backgroundImage: "",
    buttons: [] as HeroSection["buttons"],
    isActive: true,
  });

  // Upload states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string>("");
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState<string>("");

  // Danh sách các trang hợp lệ
  const validPages = [
    { value: "home", label: "Trang chủ" },
    { value: "projects", label: "Dự án" },
    { value: "academy", label: "Academy" },
    { value: "about", label: "Về chúng tôi" },
    { value: "contact", label: "Liên hệ" },
    { value: "services", label: "Dịch vụ" },
    { value: "news", label: "Tin tức" },
    { value: "consultation", label: "Tư vấn" },
    { value: "privacy-policy", label: "Chính sách bảo mật" },
  ];

  // Fetch hero sections khi component mount
  useEffect(() => {
    fetchHeros();
  }, []);

  const fetchHeros = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getHeroForAdmin();
      if (response.success && response.data) {
        // Nếu data là array, set trực tiếp; nếu là object, convert thành array
        if (Array.isArray(response.data)) {
          setHeros(response.data);
        } else {
          // Nếu chỉ có 1 hero (trả về object), convert thành array
          setHeros([response.data]);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Không thể tải hero sections");
      console.error("Error fetching heroes:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (hero: HeroSection) => {
    setEditingHero(hero);
    setFormData({
      page: hero.page,
      badge: hero.badge || { text: "", icon: "" },
      heading: hero.heading,
      subheading: hero.subheading || "",
      description: hero.description || "",
      backgroundImage: hero.backgroundImage || "",
      buttons: hero.buttons || [],
      isActive: hero.isActive,
    });
    setPreviewImage(hero.backgroundImage || "");
    setSelectedFile(null);
    setImageError("");
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingHero(null);
    setFormData({
      page: "",
      badge: { text: "", icon: "" },
      heading: "",
      subheading: "",
      description: "",
      backgroundImage: "",
      buttons: [],
      isActive: true,
    });
    setPreviewImage("");
    setSelectedFile(null);
    setImageError("");
    setShowModal(true);
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Clear error
      if (imageError) {
        setImageError("");
      }

      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Clear URL input when file is selected
      setFormData({ ...formData, backgroundImage: "" });
    }
  };

  // Handle upload image
  const handleUploadImage = async (heroId: string) => {
    if (!selectedFile) return null;

    try {
      const response = await uploadService.uploadFile(
        selectedFile,
        heroId,
        "HeroSection"
      );
      return response.data?.url || response.url || "";
    } catch (error: any) {
      console.error("Upload error:", error);
      throw new Error(error.message || "Không thể upload ảnh");
    }
  };

  const handleSave = async () => {
    try {
      setError(null);
      setImageError("");

      // Validate số lượng buttons
      if (formData.buttons.length > 2) {
        setError("Chỉ được thêm tối đa 2 nút");
        return;
      }

      // Validate required fields
      if (!formData.page) {
        setError("Vui lòng chọn trang");
        return;
      }

      if (!formData.heading || !formData.heading.trim()) {
        setError("Vui lòng nhập heading");
        return;
      }

      let imageUrl = formData.backgroundImage;

      if (editingHero) {
        // Upload new image if selected
        if (selectedFile) {
          try {
            setUploading(true);
            imageUrl = await handleUploadImage(editingHero.id);
            if (!imageUrl) {
              setError("Không thể upload ảnh");
              return;
            }
          } catch (uploadErr: any) {
            setError(uploadErr.message || "Không thể upload ảnh");
            return;
          } finally {
            setUploading(false);
          }
        }

        // Update existing hero
        const dataToSave = {
          ...formData,
          backgroundImage: imageUrl,
        };

        const response = await updateHero(editingHero.id, dataToSave);
        if (response.success) {
          await fetchHeros();
          setShowModal(false);
          setSelectedFile(null);
          setPreviewImage("");
        }
      } else {
        // Validate: Kiểm tra page đã tồn tại chưa
        const existingHero = heros.find((h) => h.page === formData.page);
        if (existingHero) {
          setError(
            `Trang "${
              validPages.find((p) => p.value === formData.page)?.label ||
              formData.page
            }" đã có hero section. Vui lòng chỉnh sửa hero section hiện có thay vì tạo mới.`
          );
          return;
        }

        // Create new hero - tạo hero trước với ảnh hiện tại hoặc rỗng
        const createData = {
          ...formData,
          backgroundImage: imageUrl,
        };

        const response = await createHero(createData);
        const heroId = response.data?.id || response.data?._id;

        if (response.success && heroId) {
          // Upload image if selected
          if (selectedFile) {
            try {
              setUploading(true);
              imageUrl = await handleUploadImage(heroId);
              if (imageUrl) {
                // Update hero with uploaded image URL
                await updateHero(heroId, {
                  ...createData,
                  backgroundImage: imageUrl,
                });
              }
            } catch (uploadErr: any) {
              setError(uploadErr.message || "Không thể upload ảnh");
              return;
            } finally {
              setUploading(false);
            }
          }

          await fetchHeros();
          setShowModal(false);
          setSelectedFile(null);
          setPreviewImage("");
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Không thể lưu hero section");
      console.error("Error saving hero:", err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Bạn có chắc chắn muốn xóa hero section này?")) {
      return;
    }

    try {
      setError(null);
      const response = await deleteHero(id);
      if (response.success) {
        await fetchHeros();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Không thể xóa hero section");
      console.error("Error deleting hero:", err);
    }
  };

  const toggleActive = async (hero: HeroSection) => {
    try {
      setError(null);
      const response = await updateHero(hero.id, {
        ...hero,
        isActive: !hero.isActive,
      });
      if (response.success) {
        await fetchHeros();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Không thể cập nhật trạng thái");
      console.error("Error toggling active:", err);
    }
  };

  const addButton = () => {
    // Giới hạn tối đa 2 nút
    if (formData.buttons.length >= 2) {
      alert("Chỉ được thêm tối đa 2 nút");
      return;
    }

    setFormData({
      ...formData,
      buttons: [
        ...formData.buttons,
        {
          text: "",
          url: "",
          actionType: "url",
          style: "primary",
          order: formData.buttons.length,
          isVisible: true,
        },
      ],
    });
  };

  const removeButton = (index: number) => {
    setFormData({
      ...formData,
      buttons: formData.buttons.filter((_, i) => i !== index),
    });
  };

  const updateButton = (index: number, field: string, value: any) => {
    const newButtons = [...formData.buttons];
    newButtons[index] = { ...newButtons[index], [field]: value };
    setFormData({ ...formData, buttons: newButtons });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#2A4B7C]"></div>
        <p className="mt-4 text-gray-600">Đang tải hero sections...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">
          Quản lý Hero Sections
        </h2>
        <button
          onClick={handleAdd}
          className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line mr-2"></i>
          Thêm Hero Section
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trang
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nội dung
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trạng thái
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {heros.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    Chưa có hero section nào. Hãy thêm mới.
                  </td>
                </tr>
              ) : (
                heros.map((hero) => (
                  <tr key={hero.id}>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {validPages.find((p) => p.value === hero.page)?.label ||
                          hero.page}
                      </div>
                      <div className="text-sm text-gray-500">{hero.page}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        {hero.backgroundImage && (
                          <img
                            src={hero.backgroundImage}
                            alt={hero.heading}
                            className="w-20 h-12 object-cover rounded border border-gray-200 flex-shrink-0"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                            {hero.heading}
                          </div>
                          <div className="text-sm text-gray-500 max-w-xs truncate">
                            {hero.subheading}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            {hero.buttons.length} nút |{" "}
                            {hero.backgroundImage ? "Có ảnh" : "Không có ảnh"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleActive(hero)}
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full cursor-pointer whitespace-nowrap ${
                          hero.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {hero.isActive ? "Hiển thị" : "Ẩn"}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEdit(hero)}
                        className="text-teal-600 hover:text-teal-900 mr-3 cursor-pointer"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                      <button
                        onClick={() => handleDelete(hero.id)}
                        className="text-red-600 hover:text-red-900 cursor-pointer"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingHero
                  ? "Chỉnh sửa Hero Section"
                  : "Thêm Hero Section mới"}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              {/* Page Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trang <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.page}
                  onChange={(e) => {
                    const newPage = e.target.value;
                    // Nếu chuyển từ home sang trang khác, xóa backgroundImage
                    // Nếu chuyển từ trang khác sang home, giữ nguyên backgroundImage
                    setFormData({
                      ...formData,
                      page: newPage,
                      backgroundImage:
                        newPage === "home" ? formData.backgroundImage : "",
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  disabled={!!editingHero}
                >
                  <option value="">Chọn trang</option>
                  {validPages.map((page) => {
                    // Hiển thị các trang chưa có hero section hoặc trang đang edit
                    const existingHero = heros.find(
                      (h) => h.page === page.value
                    );
                    const isCurrentEditingPage =
                      editingHero?.page === page.value;
                    const canSelect = !existingHero || isCurrentEditingPage;

                    return (
                      <option
                        key={page.value}
                        value={page.value}
                        disabled={!canSelect && !editingHero}
                      >
                        {page.label}{" "}
                        {!canSelect && !editingHero
                          ? "(Đã có hero section)"
                          : ""}
                      </option>
                    );
                  })}
                </select>
                {formData.page && !editingHero && (
                  <p className="mt-1 text-xs text-gray-500">
                    {heros.find((h) => h.page === formData.page)
                      ? "⚠️ Trang này đã có hero section. Vui lòng chỉnh sửa hero section hiện có."
                      : "✓ Trang này chưa có hero section. Bạn có thể tạo mới."}
                  </p>
                )}
              </div>

              {/* Badge */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Badge Text
                  </label>
                  <input
                    type="text"
                    value={formData.badge.text}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        badge: { ...formData.badge, text: e.target.value },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Nhập badge text..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Badge Icon
                  </label>
                  <input
                    type="text"
                    value={formData.badge.icon}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        badge: { ...formData.badge, icon: e.target.value },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="ri-graduation-cap-line"
                  />
                </div>
              </div>

              {/* Heading */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Heading <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.heading}
                  onChange={(e) =>
                    setFormData({ ...formData, heading: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="StackWay"
                  required
                />
              </div>

              {/* Subheading */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subheading
                </label>
                <input
                  type="text"
                  value={formData.subheading}
                  onChange={(e) =>
                    setFormData({ ...formData, subheading: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="GIẢI PHÁP CÔNG NGHỆ TOÀN DIỆN"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Mô tả chi tiết..."
                  maxLength={2000}
                />
              </div>

              {/* Background Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Background Image
                </label>

                  {/* File Upload */}
                  <div className="mb-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                        imageError ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    {selectedFile && !imageError && (
                      <p className="text-sm text-green-600 mt-1">
                        <i className="ri-check-line mr-1"></i>
                        Đã chọn: {selectedFile.name}
                      </p>
                    )}
                    {imageError && (
                      <p className="text-sm text-red-600 mt-1">{imageError}</p>
                    )}
                    {!selectedFile && (
                      <p className="text-xs text-gray-500 mt-1">
                        Hoặc nhập URL bên dưới
                      </p>
                    )}
                  </div>

                  {/* URL Input */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Hoặc nhập URL ảnh
                    </label>
                    <input
                      type="url"
                      value={formData.backgroundImage}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          backgroundImage: e.target.value,
                        });
                        // Clear file when URL is entered
                        if (e.target.value) {
                          setSelectedFile(null);
                          setPreviewImage("");
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>

                  {/* Preview Image */}
                  {(previewImage || formData.backgroundImage) && (
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Preview
                      </label>
                      <div className="relative w-full border border-gray-300 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center min-h-[200px]">
                        <img
                          src={previewImage || formData.backgroundImage}
                          alt="Background preview"
                          className="max-w-full max-h-96 w-auto h-auto object-contain"
                          onError={(e) => {
                            e.currentTarget.src =
                              'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="200"%3E%3Crect fill="%23e5e7eb" width="400" height="200"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-family="Arial" font-size="14"%3EẢnh không tải được%3C/text%3E%3C/svg%3E';
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, backgroundImage: "" });
                            setSelectedFile(null);
                            setPreviewImage("");
                          }}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors cursor-pointer"
                          title="Xóa ảnh"
                        >
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  )}

                  {uploading && (
                    <div className="mt-2 text-sm text-blue-600">
                      <i className="ri-loader-4-line animate-spin mr-1"></i>
                      Đang upload ảnh...
                    </div>
                  )}
              </div>

              {/* Buttons */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Buttons{" "}
                    {formData.buttons.length > 0 &&
                      `(${formData.buttons.length}/2)`}
                  </label>
                  <button
                    type="button"
                    onClick={addButton}
                    disabled={formData.buttons.length >= 2}
                    className={`text-sm cursor-pointer ${
                      formData.buttons.length >= 2
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-teal-600 hover:text-teal-800"
                    }`}
                  >
                    <i className="ri-add-line mr-1"></i>
                    Thêm nút
                  </button>
                </div>
                {formData.buttons.length >= 2 && (
                  <p className="text-xs text-gray-500 mb-2">
                    Đã đạt giới hạn tối đa 2 nút
                  </p>
                )}

                {formData.buttons.map((button, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4 mb-3 space-y-3"
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">
                        Nút {index + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeButton(index)}
                        className="text-red-600 hover:text-red-800 cursor-pointer"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Text
                        </label>
                        <input
                          type="text"
                          value={button.text}
                          onChange={(e) =>
                            updateButton(index, "text", e.target.value)
                          }
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                          placeholder="NHẬN TƯ VẤN NGAY"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          URL
                        </label>
                        <input
                          type="text"
                          value={button.url}
                          onChange={(e) =>
                            updateButton(index, "url", e.target.value)
                          }
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                          placeholder="#contact"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Action Type
                        </label>
                        <select
                          value={button.actionType}
                          onChange={(e) =>
                            updateButton(index, "actionType", e.target.value)
                          }
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                        >
                          <option value="url">URL</option>
                          <option value="modal">Modal</option>
                          <option value="scroll">Scroll</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Style
                        </label>
                        <select
                          value={button.style}
                          onChange={(e) =>
                            updateButton(index, "style", e.target.value)
                          }
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                        >
                          <option value="primary">Primary</option>
                          <option value="secondary">Secondary</option>
                          <option value="outline">Outline</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Order
                        </label>
                        <input
                          type="number"
                          value={button.order}
                          onChange={(e) =>
                            updateButton(
                              index,
                              "order",
                              parseInt(e.target.value) || 0
                            )
                          }
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                          min="0"
                        />
                      </div>
                      <div className="flex items-center pt-6">
                        <input
                          type="checkbox"
                          checked={button.isVisible}
                          onChange={(e) =>
                            updateButton(index, "isVisible", e.target.checked)
                          }
                          className="mr-2"
                        />
                        <label className="text-xs text-gray-600">
                          Hiển thị
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Is Active */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Kích hoạt (Hiển thị trên website)
                  </span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                Hủy
              </button>
              <button
                onClick={handleSave}
                disabled={uploading}
                className={`px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer ${
                  uploading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {uploading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Đang xử lý...
                  </>
                ) : editingHero ? (
                  "Cập nhật"
                ) : (
                  "Thêm mới"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroSectionManagement;
